import type { FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { useLocation } from "react-router-dom";
import type { PlainWorkflowExecutionAction } from "@/queries/automation-queries";
import { workflowExecutionsUrl } from "@/utils/automations";
import { SidebarButton } from "@/components/SidebarButton";
import IconPageTextPieChart from "central-icons/IconPageTextPieChart";
import { WorkflowExecutionActionGroup } from "@/components/automations/AutomationExecutionActionGroup";
import { Counter } from "@/components/flexkit/badge/Counter";
import { ActionGroupType, groupActions } from "@/utils/automation-execution-action";

type WorkflowExecutionSidebarProps = {
    workflowExecutionId: string;
    actions: PlainWorkflowExecutionAction[];
};

const groupOrder: ActionGroupType[] = [
    ActionGroupType.Active,
    ActionGroupType.Failed,
    ActionGroupType.Completed,
    ActionGroupType.Stopped,
    ActionGroupType.Deleted,
    ActionGroupType.Pending,
];

export const WorkflowExecutionSidebar: FC<WorkflowExecutionSidebarProps> = ({ workflowExecutionId, actions }) => {
    const location = useLocation();
    const summaryUrl = workflowExecutionsUrl({ id: workflowExecutionId });
    const isSummaryActive = location.pathname === summaryUrl;
    const groupedActions = groupActions(actions);

    return (
        <div className="flex flex-col gap-4">
            <SidebarButton
                to={summaryUrl}
                label="Summary"
                active={isSummaryActive}
                icon={<IconPageTextPieChart size="16" />}
            />

            <div className="flex flex-col gap-1">
                <div className="flex items-center">
                    <Text className="pl-2 text-sm text-content-strong">Actions</Text>
                    <Counter counter={`${actions.length}`} variant={"default"} />
                </div>
                {groupOrder.map((groupType) => {
                    const group = groupedActions.get(groupType);
                    if (!group || group.actions.length === 0) {
                        return null;
                    }
                    return (
                        <WorkflowExecutionActionGroup
                            key={groupType}
                            groupType={groupType}
                            actions={group.actions}
                            workflowExecutionId={workflowExecutionId}
                        />
                    );
                })}
            </div>
        </div>
    );
};
